// src/lib/data.js
import { supabase } from './supabase';

const handle = ({ data, error }, context = {}) => {
  if (error) {
    console.error('Supabase request failed:', context, error);
    throw error;
  }
  return data;
};

async function listRows(table, orderCol = 'created_at', asc = false) {
  return handle(await supabase.from(table).select('*').order(orderCol, { ascending: asc }), { table, operation: 'select' });
}

async function insertRow(table, row) {
  const { data: { user } } = await supabase.auth.getUser();
  const payload = { ...row, user_id: user.id };
  return handle(await supabase.from(table).insert(payload).select().single(), { table, operation: 'insert', payload });
}

function mapCycleInsert(row) {
  const entry = { ...row };
  if (entry.startDate !== undefined) {
    entry.start_date = entry.startDate;
    delete entry.startDate;
  }
  if (entry.endDate !== undefined) {
    entry.end_date = entry.endDate;
    delete entry.endDate;
  }
  return entry;
}

function mapCyclePatch(patch) {
  const entry = { ...patch };
  if (entry.startDate !== undefined) {
    entry.start_date = entry.startDate;
    delete entry.startDate;
  }
  if (entry.endDate !== undefined) {
    entry.end_date = entry.endDate;
    delete entry.endDate;
  }
  return entry;
}

function toCycleClient(row) {
  if (!row) return row;
  const { start_date, end_date, ...rest } = row;
  return {
    ...rest,
    startDate: start_date,
    endDate: end_date,
  };
}

async function updateRow(table, id, patch) {
  return handle(await supabase.from(table).update(patch).eq('id', id).select().single(), { table, operation: 'update', id, patch });
}

async function deleteRow(table, id) {
  return handle(await supabase.from(table).delete().eq('id', id));
}

export const Compounds = {
  list: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return handle(await supabase.from('compounds').select('*').eq('user_id', user.id).order('name', { ascending: true }));
  },
  add: (c) => insertRow('compounds', c),
  update: (id, patch) => updateRow('compounds', id, patch),
  remove: (id) => deleteRow('compounds', id),
};

export const Batches = {
  list: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return handle(await supabase.from('batches').select('*').eq('user_id', user.id).order('purchase_date', { ascending: false }));
  },
  add: (b) => insertRow('batches', b),
  update: (id, patch) => updateRow('batches', id, patch),
  remove: (id) => deleteRow('batches', id),
};

export const InjectionSites = {
  list: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return handle(await supabase.from('injection_sites').select('*').eq('user_id', user.id).order('last_used', { ascending: false }));
  },
  add: (s) => insertRow('injection_sites', s),
  update: (id, patch) => updateRow('injection_sites', id, patch),
  remove: (id) => deleteRow('injection_sites', id),
};

export const LogEntries = {
  list: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return handle(await supabase.from('log_entries').select('*').eq('user_id', user.id).order('date', { ascending: false }));
  },
  listForDateRange: async (startDate, endDate) => {
    const { data: { user } } = await supabase.auth.getUser();
    return handle(await supabase.from('log_entries')
      .select('*')
      .eq('user_id', user.id)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true }));
  },
  add: (l) => insertRow('log_entries', l),
  bulkAdd: async (rows) => {
    const { data: { user } } = await supabase.auth.getUser();
    const payload = rows.map(r => ({ ...r, user_id: user.id }));
    return handle(await supabase.from('log_entries').insert(payload).select(), { table: 'log_entries', operation: 'bulk insert', payload });
  },
  update: (id, patch) => updateRow('log_entries', id, patch),
  remove: (id) => deleteRow('log_entries', id),
  removeAllForCycle: async (cycleId) => {
    return handle(await supabase.from('log_entries').delete().eq('cycle_id', cycleId));
  },
};
export const BloodResults = {
  list: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return handle(await supabase.from('blood_results').select('*').eq('user_id', user.id).order('date', { ascending: false }));
  },
  add: (b) => insertRow('blood_results', b),
  remove: (id) => deleteRow('blood_results', id),
};

export const Notes = {
  list: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return handle(await supabase.from('notes').select('*').eq('user_id', user.id).order('date', { ascending: false }));
  },
  add: (n) => insertRow('notes', n),
  remove: (id) => deleteRow('notes', id),
};

export const Cycles = {
  list: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return handle(await supabase.from('cycles').select('*').eq('user_id', user.id).order('start_date', { ascending: false }));
  },
  add: (c) => insertRow('cycles', c),
  update: (id, patch) => updateRow('cycles', id, patch),
  remove: (id) => deleteRow('cycles', id),
};

export const CycleDoses = {
  listForCycle: async (cycleId) => {
    return handle(await supabase.from('cycle_doses').select('*').eq('cycle_id', cycleId).order('start_date', { ascending: true }));
  },
  add: (cd) => insertRow('cycle_doses', cd),
  update: (id, patch) => updateRow('cycle_doses', id, patch),
  remove: (id) => deleteRow('cycle_doses', id),
  removeAllForCycle: async (cycleId) => {
    return handle(await supabase.from('cycle_doses').delete().eq('cycle_id', cycleId));
  },
};

export const BodyEntries = {
  list: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return handle(await supabase.from('body_entries').select('*').eq('user_id', user.id).order('date', { ascending: true }));
  },
  add: (b) => insertRow('body_entries', b),
  remove: (id) => deleteRow('body_entries', id),
};

export const Goal = {
  get: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase.from('goals').select('*').eq('user_id', user.id).maybeSingle();
    if (error) throw error;
    return data;
  },
  save: async (patch) => {
    const { data: { user } } = await supabase.auth.getUser();
    return handle(await supabase.from('goals').upsert({ user_id: user.id, ...patch }).select().single());
  },
};

export const CompoundLibrary = {
  list: async () => {
    const data = await listRows('compound_library', 'name', true);
    return data.filter(c => c.active !== false);
  },
};